import { type ReactNode, useEffect } from "react";
import { Box, type TabProps, Tabs } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useGetAccountAccess } from "@dealer-center/ddc-pages-configuration-fetch";
import { AccountAccessTab } from "./components/AccountAccess/AccountAccessTab";
import { hasAccountHierarchy } from "./components/AccountAccess/hasAccountHierarchy";
import { FeatureAccessTab } from "./components/FeatureAccess/FeatureAccessTab";
import { LoginHistoryTab } from "./components/LoginHistory/LoginHistoryTab";
import { HeaderActions } from "./components/PageHeader/HeaderActions";
import { IdentityHeader } from "./components/PageHeader/IdentityHeader";
import { PageHeading } from "./components/PageHeader/PageHeading";
import { StatusRegion } from "./components/StatusRegion/StatusRegion";
import { UserInformationTab } from "./components/UserInformation/UserInformationTab";
import { useUserInfoEdits } from "./components/UserInformation/useUserInfoEdits";
import {
  BORDER_MUTED,
  SPACE_BLOCK,
  SPACE_SECTION,
  SURFACE_DEFAULT,
  SURFACE_MUTED
} from "./layoutTokens";
import { useMyInfoSubject } from "./useMyInfoSubject";
import { type MyInfoTabId, useMyInfoTabs } from "./useMyInfoTabs";

const TABS_ID = "myinfo-tabs";

/**
 * The My Info page shell: one subject, the identity header, and the tab set.
 *
 * `useMyInfoSubject` resolves the subject **once** here and every tab receives it as a prop. No hook
 * below this point re-reads the outlet context (contracts/fetch-hooks.md invariant 4) — two readers
 * could drift, and in an "as" context drifting means showing one user's header over another's data.
 *
 * The subject hook **throws** when the outlet context is absent rather than returning a partial
 * subject. That is not a case this component handles: `PagesControlcenterApp` renders a loading page
 * instead of the `<Outlet />` until `ccUserInfo` exists, and `Configuration` forwards that context
 * through (anchored by `routes.spec.tsx`). A throw here means the page was mounted outside
 * ControlCenter, which is a wiring error and should be loud, not a blank form.
 */
export const MyInfo = () => {
  const subject = useMyInfoSubject();

  /*
   * FR-006's conditional Account Access tab, sequenced per research R-007.
   *
   * The query lives **here**, not in the tab, because the tab strip has to render before it resolves:
   * `accountaccess` is the slowest endpoint (~5–6s), and gating the strip on it would leave the page
   * empty for seconds — which the Edge Cases forbid outright ("never a momentarily empty tab"). So the
   * tab appears immediately in a loading state and is removed **only once the query has resolved** to
   * "no hierarchy". A single-account user therefore sees a tab that then disappears; that was accepted
   * deliberately as the lesser evil.
   *
   * `isSuccess` is the gate, not `!isLoading`: on **failure** the tab stays. A failed read tells us
   * nothing about whether a hierarchy exists, and removing the tab would report "you have no account
   * hierarchy" — inventing an answer from an absent response. The tab stays and shows its own failure,
   * which also keeps SC-010 honest.
   *
   * React Query dedupes this against the tab's own identical query key, so mounting the tab costs no
   * second request.
   */
  const accountAccess = useGetAccountAccess({
    userId: subject.userId,
    accountId: subject.accountId
  });
  const isAccountAccessUnavailable =
    accountAccess.isSuccess && !hasAccountHierarchy(accountAccess.data);

  const { tabs, activeTabId, selectTab } = useMyInfoTabs({
    unavailableTabIds: isAccountAccessUnavailable ? ["account"] : []
  });

  /*
   * Pending User Information edits, owned **here** rather than in the tab.
   *
   * FR-009: "Switching tabs MUST preserve pending edits on User Information, MUST NOT discard them."
   * The panel handed to an inactive tab below is an empty fragment, so `UserInformationTab` unmounts
   * on every tab switch — state held inside it would vanish the moment the user opened Login History.
   * Owning it at the page level is what makes the guarantee hold. See `useUserInfoEdits`.
   */
  const userInfoEdits = useUserInfoEdits();

  /*
   * Building all four elements is cheap and mounts nothing — a JSX element is a descriptor, and only
   * the one handed to the active tab below is ever rendered. That matters: mounting every panel would
   * fire all four queries on page load, including `loginhistory` at ~20s, for tabs the user may never
   * open.
   *
   * All four panels are built as of Phase 6.
   */
  const panels: Record<MyInfoTabId, ReactNode> = {
    userinfo: <UserInformationTab edits={userInfoEdits} subject={subject} />,
    account: <AccountAccessTab subject={subject} />,
    feature: <FeatureAccessTab subject={subject} />,
    login: <LoginHistoryTab subject={subject} />
  };

  /*
   * Interstate renders a `role="tabpanel"` for every tab carrying a truthy `component`, hiding the
   * inactive ones — so each tab needs *something* there to stay selectable (its `value` is dropped
   * otherwise), and the active one gets the real content.
   */
  const interstateTabs: TabProps[] = tabs.map(tab => ({
    active: tab.id === activeTabId,
    component: tab.id === activeTabId ? panels[tab.id] : <></>,
    id: tab.id,
    label: tab.label,
    value: tab.id
  }));

  // Interstate's Tabs permanently misreports its scroll-chevron overflow check, so recompute it ourselves and toggle the buttons directly via their stable analytics attribute.
  useEffect(() => {
    const root = document.getElementById(TABS_ID);
    const scroller = root?.querySelector<HTMLElement>(".MuiTabs-scroller");
    if (!root || !scroller) return undefined;

    const updateScrollButtonVisibility = () => {
      // Either button may be absent; match each by its DOM position relative to the scroller, not CSS offsets (which real browsers can resolve to a used pixel value on both sides).
      const arrowButtons = Array.from(
        root.querySelectorAll<HTMLElement>(
          ":scope > [data-ids-react-component-name='ids-react-noTag']"
        )
      );
      const startButton = arrowButtons.find(
        button =>
          scroller.compareDocumentPosition(button) &
          Node.DOCUMENT_POSITION_PRECEDING
      );
      const endButton = arrowButtons.find(
        button =>
          scroller.compareDocumentPosition(button) &
          Node.DOCUMENT_POSITION_FOLLOWING
      );
      const canScrollLeft = scroller.scrollLeft > 1;
      const canScrollRight =
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
      if (startButton) {
        startButton.style.display = canScrollLeft ? "" : "none";
      }
      if (endButton) {
        endButton.style.display = canScrollRight ? "" : "none";
      }
    };

    updateScrollButtonVisibility();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateScrollButtonVisibility)
        : undefined;
    resizeObserver?.observe(root);

    const mutationObserver =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(updateScrollButtonVisibility)
        : undefined;
    mutationObserver?.observe(root, {
      childList: true,
      subtree: true
    });

    scroller.addEventListener("scroll", updateScrollButtonVisibility);

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      scroller.removeEventListener("scroll", updateScrollButtonVisibility);
    };
    // `tabs` is a new array every render, incl. every User Information keystroke — key off tab ids instead so this doesn't tear down/recreate the observers needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.map(tab => tab.id).join(",")]);

  return (
    <Box
      data-testid={"myinfo-page"}
      sx={{
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        minHeight: 0
      }}
    >
      <PageHeading />

      {/*
       * The card. Identity row, tab strip, and panels are one bordered surface on the app
       * background, per the approved prototype's `.um-detail` — which is what makes the tabs read as
       * belonging to this subject rather than floating on the page.
       */}
      <Box
        data-testid={"myinfo-card"}
        sx={{
          backgroundColor: SURFACE_DEFAULT,
          border: `1px solid ${BORDER_MUTED}`,
          borderRadius: "8px",
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden"
        }}
      >
        {/*
         * FR-008's single page-header action slot — in the card's first row, opposite the identity,
         * so the one Save is visible from every tab and no tab needs its own. Both actions render
         * inactive (FR-052).
         *
         * `flexWrap` plus `marginLeft: auto` rather than `justifyContent: space-between`: at a narrow
         * width the actions drop below the identity and stay left-aligned with it, whereas
         * `space-between` would strand them against the right edge on their own line.
         */}
        <Box
          sx={{
            alignItems: "center",
            borderBottom: `1px solid ${SURFACE_MUTED}`,
            columnGap: SPACE_BLOCK,
            display: "flex",
            flexShrink: 0,
            flexWrap: "wrap",
            padding: SPACE_SECTION,
            rowGap: SPACE_BLOCK
          }}
        >
          <IdentityHeader subject={subject} />

          <Box
            data-testid={"myinfo-header-actions"}
            sx={{ marginLeft: "auto" }}
          >
            <HeaderActions />
          </Box>
        </Box>

        {/*
         * T069's shared live region wraps the tab set, so it is mounted on page load and survives every
         * tab switch — a region that mounts carrying its first message is the one least likely to be
         * announced. Each tab writes to it through `TabState`; see `StatusRegion`.
         *
         * The strip is inset to align with the identity row above it and the panel content below it;
         * `TabState` owns the panels' own padding, since Interstate renders the panel element itself.
         */}
        <StatusRegion>
          {/*
           * The tab strip spans the card edge to edge, so its underline reads as the card's own
           * divider rather than a rule floating inside it — the prototype's `.tabs` treatment.
           *
           * The margin reset is not cosmetic tidying. Interstate gives every `role="tabpanel"` its
           * own `margin-left: 8px; margin-top: 16px`, which stacks on top of `TabState`'s padding and
           * left every tab's content 8px to the right of the identity row above it — a misalignment
           * that reads as a rendering bug precisely because it is small. Zeroing it here makes
           * `TabState`'s padding the single source of the panels' inset. Targeted by `role` rather
           * than by class name, so it survives an Interstate class-hash change.
           *
           * The flex/overflow rules on this box and on `[role='tabpanel']` are what let the tab
           * content scroll on its own while the tab strip above it stays put: Interstate renders the
           * tab bar and each panel as flat siblings with no height/flex styling of its own (there is
           * no prop for it), so this box has to both establish the flex column and hand the panel the
           * remaining space. The tab bar keeps its natural size as the other flex child.
           */}
          <Box
            sx={{
              display: "flex",
              flex: "1 1 auto",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
              "& [role='tabpanel']": {
                flex: "1 1 auto",
                marginLeft: 0,
                marginTop: 0,
                minHeight: 0,
                overflowY: "auto"
              }
            }}
          >
            <Tabs
              ariaLabel={Locale.getText("TABS_ARIA_LABEL") as string}
              id={TABS_ID}
              tabs={interstateTabs}
              onActivate={selectTab}
            />
          </Box>
        </StatusRegion>
      </Box>
    </Box>
  );
};
